
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AssetClassification } from "@/types/classifications";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { logger } from "@/services/LoggingService";

interface AssetClassificationEditorProps {
  classification: AssetClassification;
  onSave: (classification: AssetClassification) => void;
  onCancel: () => void;
}

export function AssetClassificationEditor({ 
  classification, 
  onSave, 
  onCancel 
}: AssetClassificationEditorProps) {
  const [editedClassification, setEditedClassification] = React.useState<AssetClassification>({
    ...classification,
    status: classification.status || 'draft'
  });

  const handleStatusChange = (newStatus: string) => {
    logger.debug('classifications.editor', 'Status change requested:', {
      oldStatus: editedClassification.status,
      newStatus,
      timestamp: new Date().toISOString()
    });

    setEditedClassification(prev => ({
      ...prev,
      status: newStatus as AssetClassification['status']
    }));
  };

  return (
    <div className="space-y-6 py-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="display_name">Display Name</Label>
          <Input
            id="display_name"
            value={editedClassification.display_name}
            onChange={(e) => setEditedClassification(prev => ({
              ...prev,
              display_name: e.target.value
            }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="reference_name">Reference Name</Label>
          <Input
            id="reference_name"
            value={editedClassification.reference_name}
            onChange={(e) => setEditedClassification(prev => ({
              ...prev,
              reference_name: e.target.value
            }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={editedClassification.status}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger id="status" className="w-full bg-background">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent 
              position="popper" 
              className="z-[1000] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
            >
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="deprecated">Deprecated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="provider_type">Provider Type</Label>
          <Input
            id="provider_type"
            value={editedClassification.provider_type || ''}
            onChange={(e) => setEditedClassification(prev => ({
              ...prev,
              provider_type: e.target.value
            }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Input
            id="notes"
            value={editedClassification.notes || ''}
            onChange={(e) => setEditedClassification(prev => ({
              ...prev,
              notes: e.target.value
            }))}
          />
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSave(editedClassification)}>
          Save Changes
        </Button>
      </div>
    </div>
  );
}
